import type React from 'react';
import { memo } from 'react';
import styled, { css } from 'styled-components';

import { BASE_FONT_SIZE } from '@wecasa/common/src/constants';
import type { FontWeight } from '@wecasa/common/src/types/components';
import colors from '@wecasa/common/src/utils/colors';
import { parseText } from '@wecasa/common/src/utils/strings';
import { toRem } from '@wecasa/common/src/utils/styles';

import { TEXT_FONT } from '../../constants/styles';

// @deprecated
export const FONT_WEIGHTS = Object.freeze({
  REGULAR: 400,
  SEMI_BOLD: 500,
  BOLD: 700,
  BLACK: 900,
});

const defaultColor = colors.ink100;
const defaultFontSize = BASE_FONT_SIZE;
const defaultLineHeight = 1.333;
const defaultFontWeight: FontWeight = 400;
const defaultLetterSpacing = 'normal';

const TextsContainer = styled.span<{
  $display?: string;
  $centered: boolean;
  $lineHeight?: number;
  $fontSize?: number;
}>`
  display: ${(props) => props.$display};
  text-align: ${(props) => (props.$centered ? 'center' : 'left')};
  line-height: ${(props) => props.$lineHeight || defaultLineHeight};
  font-size: ${(props) => toRem(props.$fontSize) || defaultFontSize};
`;

export type TextStyleProps = {
  $display?: string;
  $fontSize?: number;
  $fontWeight?: FontWeight;
  $lineHeight?: number;
  $centered?: boolean;
  $color?: string;
  $letterSpacing?: string;
  $strikedOut?: boolean;
  $isSup?: boolean;
  $isItalic?: boolean;
};

export const textStyle = css<TextStyleProps>`
  font-family: ${TEXT_FONT};
  ${(props) => `
    font-size: ${toRem(props.$fontSize || defaultFontSize)};
    font-weight: ${props.$fontWeight || defaultFontWeight};
    line-height: ${props.$lineHeight || defaultLineHeight};
    text-align: ${props.$centered ? 'center' : 'left'};
    color: ${props.$color || defaultColor};
    letter-spacing: ${props.$letterSpacing || defaultLetterSpacing};
    text-decoration-line: ${props.$strikedOut ? 'line-through' : 'none'};
  ${
    props.$isSup &&
    `
      vertical-align: super;
      font-size: ${toRem((props.$fontSize || defaultFontSize) * 0.7)};
      line-height: 0;
    `
  };
    font-style: ${props.$isItalic ? 'italic' : 'normal'};
  `};
`;

const StyledText = styled.span<TextStyleProps>`
  display: ${(props) => props.$display};
  ${textStyle}
`;

export type TextProps = {
  id?: string;
  display?: 'block' | 'inline' | 'inline-block' | 'flex';
  color?: string;
  fontSize?: number;
  lineHeight?: number;
  fontWeight?: FontWeight;
  centered?: boolean;
  className?: string;
  htmlTag?: React.ElementType;
  children: string | number | React.ReactNode;
  letterSpacing?: string;
  strikedOut?: boolean;
};

const BaseText = ({
  id,
  children = '',
  color = defaultColor,
  fontSize = defaultFontSize,
  fontWeight = defaultFontWeight,
  lineHeight = defaultLineHeight,
  centered = false,
  className,
  htmlTag,
  display,
  letterSpacing,
  strikedOut = false,
}: TextProps) => {
  const textChildren = children ?? ''; // if children is `null`, defaultProps ('') will not be used.
  const parsedText = parseText(String(textChildren));

  if (!parsedText.length) return null;
  // In case we want to render children that are different from string or number
  // we don't want to parse them, we want to render them as they are but still stay consistent in our style
  if (!['string', 'number'].includes(typeof children)) {
    return (
      <StyledText
        $fontWeight={fontWeight}
        $color={color}
        $fontSize={fontSize}
        $lineHeight={lineHeight}
        $letterSpacing={letterSpacing}
        $strikedOut={strikedOut}
        className={className}
        as={htmlTag}
      >
        {children}
      </StyledText>
    );
  }
  /*
   * We set a div container if parsing returns multiple texts so that they are
   * rendered side by side, like a standard sentence. Without it, each text would
   * be rendered as a unique child of the parent component, and in this case each of
   * them would inherit its display property, it could lead to unwanted / broken layouts.
   */
  // TODO: Check if we can move className to TextsContainer because for me the styles should be applied to this element
  return parsedText.length > 1 ? (
    <TextsContainer
      id={id}
      as={htmlTag}
      $display={display || 'block'}
      $centered={centered}
      $fontSize={fontSize}
      $lineHeight={lineHeight}
    >
      {parsedText.map((text, index) => (
        <StyledText
          // biome-ignore lint/suspicious/noArrayIndexKey: reason
          key={index}
          $fontWeight={text.bold ? 700 : fontWeight}
          $color={color}
          $fontSize={fontSize}
          $lineHeight={lineHeight}
          $letterSpacing={letterSpacing}
          $strikedOut={strikedOut}
          $isSup={text.sup}
          $isItalic={text.italic}
          className={className}
        >
          {text.text}
        </StyledText>
      ))}
    </TextsContainer>
  ) : (
    <StyledText
      id={id}
      $fontWeight={parsedText[0].bold ? 700 : fontWeight}
      $color={color}
      $fontSize={fontSize}
      $lineHeight={lineHeight}
      $centered={centered}
      $strikedOut={strikedOut}
      className={className}
      as={htmlTag}
      $display={display}
    >
      {parsedText[0].text}
    </StyledText>
  );
};

export default memo(BaseText);
